import React from "react";
import {
  ColumnDef,
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import ActionDropdown from "./ActionDropdown"; // Custom dropdown
import { Checkbox } from "./ui/checkbox";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { PageButtons } from "./PageButtons";

export type DataTableProps<T> = {
  columns: ColumnDef<T>[];
  data: T[];
  actions: (row: T) => {
    label: string;
    icon: React.ReactNode;
    variant: "default" | "destructive" | "outline" | "secondary"; // Add other variants as needed
    onClick: (row: T) => void;
  }[];
  disableCheckboxes?: boolean; // New prop to disable checkboxes
  currentUserId?: string;
};

export function DataTable<T extends { contributorId?: string }>({
  columns,
  data,
  actions,
  showActions = true, // Default to true
  disableCheckboxes = false, // Default to false
  currentUserId,
}: DataTableProps<T> & { showActions?: boolean }) {
  const [globalFilter, setGlobalFilter] = React.useState("");

  const tableColumns = React.useMemo(() => {
    // Avoid adding duplicate "Actions" columns
    const hasActionsColumn = columns.some((col) => col.id === "actions");

    const baseColumns = disableCheckboxes
      ? columns
      : [
          {
            id: "select",
            header: ({ table }) => (
              <Checkbox
                checked={
                  table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) =>
                  table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all"
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
              />
            ),
            enableSorting: false,
            enableHiding: false,
          },
          ...columns,
        ];

    if (showActions && !hasActionsColumn) {
      baseColumns.push({
        id: "actions",
        header: () => <div className="font-bold text-gray-900">Actions</div>,
        cell: ({ row }) => (
          <ActionDropdown
            actions={actions(row.original).map((action) => ({
              ...action,
              variant: action.variant || "dropPlain",
              onClick: () => action.onClick(row.original),
            }))}
          />
        ),
      });
    }

    return baseColumns;
  }, [columns, showActions, actions, disableCheckboxes]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRows = table.getSelectedRowModel().rows;

  return (
    <div className="w-full">
      {/* Filter input */}
      <div className="flex items-center py-4">
        <Input
          placeholder="Search..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Bulk Action Toolbar */}
      {selectedRows.length >= 1 && (
        <div className="w-full rounded-lg bg-white p-4 shadow-md border mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between ">
          {/* Number of rows selected */}
          <div className="text-sm text-muted-foreground">
            {selectedRows.length} row(s) selected
          </div>

          {/* Bulk action buttons */}
          <div className="flex flex-col md:flex-row md:space-x-2 gap-2 md:justify-end">
            {actions(selectedRows[0].original).map((action, index) => (
              <Button
                key={index}
                variant={action.variant ?? "dropPlain"}
                size="sm"
                onClick={() => {
                  selectedRows.forEach((row) => {
                    action.onClick(row.original);
                  });
                }}
              >
                {action.icon} {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Responsive Table Container */}
      <div className="mx-auto overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={`font-bold text-gray-900 ${
                      header.column.getCanSort()
                        ? "cursor-pointer select-none"
                        : ""
                    }`}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <div className="w-4 h-4 flex items-center justify-center">
                            {header.column.getIsSorted() === "asc" && (
                              <ArrowUp className="w-4 h-4 text-black" />
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <ArrowDown className="w-4 h-4 text-black" />
                            )}
                            {header.column.getIsSorted() === false && (
                              <ArrowUpDown className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isCurrentUser =
                  currentUserId && row.original.contributorId === currentUserId;
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={isCurrentUser ? "bg-blue-50 font-medium" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`whitespace-nowrap text-sm ${
                          isCurrentUser ? "text-blue-600" : ""
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length} // Adjust dynamically based on columns
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Responsive Pagination */}
      <div className="flex flex-col items-start gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page</span>
          <select
            className="border rounded px-2 py-1 text-sm bg-white"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {[5, 10, 20, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground ml-4">
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}
            {" - "}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{" "}
            of {table.getFilteredRowModel().rows.length}
          </span>
        </div>
        <div className="space-x-2 flex items-center">
          {/* Left ellipsis if needed */}
          {(() => {
            // Add ChevronLeft and ChevronRight outside this block
            return [
              <Button
                key="chevron-left"
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>,
              <PageButtons table={table} />,
              <Button
                key="chevron-right"
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>,
            ];
          })()}
        </div>
      </div>
    </div>
  );
}
